/**
 * Iter 20.23 — Rückfragen-Sektion in Schritt 2
 *
 * Sortiert die Rückfragen bereits per Backend nach Impact
 * (high → medium → low). Diese Component:
 *   - rendert die high/medium Fragen expandiert
 *   - klappt low-impact Fragen initial ein („Noch N optionale Rückfragen")
 *   - baut den Q&A-String für den Reanalyze-Call
 *
 * Rückwärts-kompatibel: LLM-Antworten mit reinen Strings (alte Version)
 * werden im Backend zu `{type: "short_text", impact: "medium"}` gecastet,
 * dann ganz normal von `ClarifyingQuestion` gerendert.
 */
import React, { useMemo, useState } from "react";
import { ArrowsClockwise, Sparkle, CaretDown } from "@phosphor-icons/react";
import { Button } from "../../components/ui/Button";
import ClarifyingQuestion from "./ClarifyingQuestion";
import { NOTE_DELIM } from "./answerValue";

export default function ClarifyingQuestionsSection({
  questions,
  answers,
  setAnswers,
  reanalyzing,
  onReanalyze,
}) {
  // Iter 20.23 — Split in „primary" (high + medium) und „low" (feine Nuance).
  // Backend liefert bereits impact-sortiert, wir nutzen nur die Kategorien.
  const { primary, low } = useMemo(() => {
    const p = [];
    const l = [];
    (questions || []).forEach((q, origIndex) => {
      const impact = (q && q.impact) || "medium";
      // Original-Index bewahren, damit answers-State stabil bleibt.
      const item = { q, origIndex };
      if (impact === "low") l.push(item);
      else p.push(item);
    });
    return { primary: p, low: l };
  }, [questions]);

  const [showLow, setShowLow] = useState(false);

  const answeredCount = Object.values(answers).filter((a) => (a || "").trim()).length;

  const buildQA = () => {
    return (questions || [])
      .map((q, i) => {
        const questionText = (q && q.question) || "";
        const raw = (answers[i] || "").trim();
        if (!raw || !questionText) return null;
        // Iter 20.24 — Zerlege Chip-Auswahl (main) und optionale Ergänzung
        // (note), die intern per NOTE_DELIM gespeichert ist. Sende dem LLM
        // ein sauberes, lesbares Format:
        //   „Q: …\nA: Ja — Ergänzung: nur für Standard-Reisekosten"
        const idx = raw.indexOf(NOTE_DELIM);
        let answerFormatted;
        if (idx === -1) {
          answerFormatted = raw;
        } else {
          const main = raw.slice(0, idx).trim();
          const note = raw.slice(idx + NOTE_DELIM.length).trim();
          if (main && note) answerFormatted = `${main} — Ergänzung: ${note}`;
          else answerFormatted = main || `Ergänzung: ${note}`;
        }
        return `Q: ${questionText}\nA: ${answerFormatted}`;
      })
      .filter(Boolean)
      .join("\n\n");
  };

  return (
    <div data-testid="tool-result-questions">
      <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500">
          Spezifische Rückfragen
        </div>
        <div className="text-[11px] text-warm-500">
          Beantworten Sie was Sie können — leere Felder werden ignoriert.
        </div>
      </div>

      <ol className="space-y-3">
        {primary.map(({ q, origIndex }) => (
          <ClarifyingQuestion
            key={origIndex}
            index={origIndex}
            question={q}
            value={answers[origIndex] || ""}
            onChange={(v) => setAnswers({ ...answers, [origIndex]: v })}
          />
        ))}
      </ol>

      {low.length > 0 && (
        <div className="mt-3">
          {!showLow ? (
            <button
              type="button"
              onClick={() => setShowLow(true)}
              data-testid="tool-questions-show-low"
              className="inline-flex items-center gap-1.5 text-[12px] text-warm-600 hover:text-brand-600 hover:underline"
            >
              <CaretDown size={12} weight="bold" />
              Noch {low.length} {low.length === 1 ? "optionale Rückfrage" : "optionale Rückfragen"} anzeigen
            </button>
          ) : (
            <ol className="space-y-3">
              {low.map(({ q, origIndex }) => (
                <ClarifyingQuestion
                  key={origIndex}
                  index={origIndex}
                  question={q}
                  value={answers[origIndex] || ""}
                  onChange={(v) => setAnswers({ ...answers, [origIndex]: v })}
                />
              ))}
            </ol>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          variant="brand"
          size="md"
          disabled={answeredCount === 0 || reanalyzing}
          onClick={() => onReanalyze(buildQA())}
          data-testid="tool-result-reanalyze"
        >
          {reanalyzing ? (
            <>
              <ArrowsClockwise size={14} weight="bold" className="animate-spin" />
              Auswertung läuft erneut …
            </>
          ) : (
            <>
              <Sparkle size={14} weight="bold" />
              Mit {answeredCount} {answeredCount === 1 ? "Antwort" : "Antworten"} neu bewerten
            </>
          )}
        </Button>
        {answeredCount === 0 && (
          <span className="text-[11px] text-warm-500">
            Geben Sie mindestens eine Antwort ein, um die Auswertung erneut anzustoßen.
          </span>
        )}
      </div>
    </div>
  );
}
